import { useState, useEffect } from 'react';
import {
  Check,
  CheckCircle2,
  Circle,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getProfilingQuestions, submitProfilingAnswers } from '../../../../../../../services/profile/profilingService';

const ProfilingTab = ({
  agentProfile,
  userData,
  hasCompletedProfiling,
  setHasCompletedProfiling,
  onProfileSaved
}) => {
  const [profilingQuestions, setProfilingQuestions] = useState([]);
  const [profilingCurrentIndex, setProfilingCurrentIndex] = useState(0);
  const [profilingAnswers, setProfilingAnswers] = useState({});
  const [isProfilingLoading, setIsProfilingLoading] = useState(false);
  const [isProfilingSubmitting, setIsProfilingSubmitting] = useState(false);
  const [isProfilingEditing, setIsProfilingEditing] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setIsProfilingLoading(true);
        const res = await getProfilingQuestions();
        if (res?.success && Array.isArray(res?.data)) {
          setProfilingQuestions(res.data);
        }
      } catch (e) {
        console.warn('Could not load profiling questions', e.message);
      } finally {
        setIsProfilingLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (agentProfile?.profiling_answers && agentProfile.profiling_answers.length > 0) {
      const map = {};
      agentProfile.profiling_answers.forEach((item) => {
        if (item.questionId) {
          map[item.questionId] = item.answer;
        }
      });
      setProfilingAnswers(map);
      setHasCompletedProfiling(true);
    }
  }, [agentProfile?.profiling_answers, setHasCompletedProfiling]);

  const handleProfilingAnswerChange = (questionId, value, type) => {
    if (type === 'multi-select') {
      const current = profilingAnswers[questionId] || [];
      if (current.includes(value)) {
        setProfilingAnswers((prev) => ({
          ...prev,
          [questionId]: current.filter((x) => x !== value)
        }));
      } else {
        setProfilingAnswers((prev) => ({
          ...prev,
          [questionId]: [...current, value]
        }));
      }
    } else {
      setProfilingAnswers((prev) => ({
        ...prev,
        [questionId]: value
      }));
    }
  };

  const handleProfilingSubmit = async () => {
    try {
      setIsProfilingSubmitting(true);
      const targetAgentId = agentProfile?._id || userData?.agent?._id || localStorage.getItem('agent_id');
      const formattedAnswers = Object.keys(profilingAnswers).map((qId) => {
        const q = profilingQuestions.find((question) => question._id === qId);
        let displayAnswer = profilingAnswers[qId];
        if (Array.isArray(displayAnswer)) {
          displayAnswer = displayAnswer.join(', ');
        }
        return {
          questionId: qId,
          question: q?.question,
          answer: displayAnswer
        };
      });
      const res = await submitProfilingAnswers(targetAgentId, formattedAnswers);
      if (res?.success) {
        toast.success('Business profile updated successfully!');
        setHasCompletedProfiling(true);
        setIsProfilingEditing(false);
        if (onProfileSaved) onProfileSaved();
      }
    } catch (err) {
      toast.error('Failed to save profile responses');
    } finally {
      setIsProfilingSubmitting(false);
    }
  };

  if (isProfilingLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2">
        <Loader2 size={22} className="animate-spin text-primary" />
        <span className="text-xs">Loading profile questions...</span>
      </div>
    );
  }

  if (hasCompletedProfiling && !isProfilingEditing) {
    return (
      <div className="flex-1 flex flex-col min-h-0 animate-in fade-in duration-200">
        <div className="shrink-0 flex items-center justify-between gap-2 mb-2">
          <p className="text-xs text-slate-500 font-normal">Your profiling answers are saved</p>
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-normal border border-emerald-200 shrink-0">
            <Check size={12} className="stroke-[2]" />
            Completed
          </span>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 p-3 rounded-2xl bg-slate-50 border border-slate-100 space-y-2 min-h-0">
          {profilingQuestions.map((q, idx) => {
            const ans = profilingAnswers[q._id];
            if (!ans) return null;
            return (
              <div key={q._id || idx} className="text-xs pb-1.5 border-b border-slate-200/50 last:border-0 last:pb-0">
                <div className="font-normal text-slate-700">{q.question}</div>
                <div className="text-primary font-normal mt-0.5 truncate">
                  {Array.isArray(ans) ? ans.join(', ') : String(ans)}
                </div>
              </div>
            );
          })}
        </div>

        <div className="shrink-0 pt-2 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setIsProfilingEditing(true);
              setProfilingCurrentIndex(0);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-normal text-slate-700 shadow-2xs cursor-pointer transition-colors"
          >
            <Edit3 size={12} className="text-primary" />
            <span>Update Responses</span>
          </button>
        </div>
      </div>
    );
  }

  if (profilingQuestions.length > 0) {
    const currentQ = profilingQuestions[profilingCurrentIndex];
    const currentVal = currentQ ? profilingAnswers[currentQ._id] : null;
    const isCurrentAnswered = (() => {
      if (!currentQ) return false;
      if (currentQ.type === 'multi-select') {
        return Array.isArray(currentVal) && currentVal.length > 0;
      }
      if (typeof currentVal === 'string') {
        return currentVal.trim().length > 0;
      }
      return currentVal !== undefined && currentVal !== null && currentVal !== '';
    })();

    return (
      <div className="flex-1 flex flex-col min-h-0 animate-in fade-in duration-200">
        {/* Stepper Header (Fixed Top) */}
        <div className="shrink-0 mb-1.5">
          <span className="text-[10px] font-normal tracking-wider text-primary uppercase">
            Question {profilingCurrentIndex + 1} of {profilingQuestions.length}
          </span>
          <h3 className="text-xs sm:text-sm font-normal text-slate-800 mt-0.5 leading-snug line-clamp-2">
            {profilingQuestions[profilingCurrentIndex]?.question}
          </h3>
        </div>

        {/* Options / Input Component (Scrollable Middle) */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-1.5 min-h-0 my-1">
          {(() => {
            if (!currentQ) return null;

            if (currentQ.type === 'select') {
              return (
                <div className="grid grid-cols-1 gap-1.5">
                  {currentQ.options?.map((opt, idx) => {
                    const isSelected = currentVal === opt;
                    return (
                      <div
                        key={idx}
                        onClick={() => handleProfilingAnswerChange(currentQ._id, opt, 'select')}
                        className={`p-2 rounded-xl border text-xs font-normal flex items-center justify-between cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-primary/10 border-primary text-primary shadow-2xs'
                            : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className="truncate mr-2">{opt}</span>
                        {isSelected ? (
                          <CheckCircle2 size={14} className="text-primary shrink-0" />
                        ) : (
                          <Circle size={14} className="text-slate-300 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            if (currentQ.type === 'multi-select') {
              return (
                <div className="grid grid-cols-1 gap-1.5">
                  {currentQ.options?.map((opt, idx) => {
                    const isSelected = currentVal?.includes(opt);
                    return (
                      <div
                        key={idx}
                        onClick={() => handleProfilingAnswerChange(currentQ._id, opt, 'multi-select')}
                        className={`p-2 rounded-xl border text-xs font-normal flex items-center justify-between cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-800 shadow-2xs'
                            : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-white hover:border-slate-300'
                        }`}
                      >
                        <span className="truncate mr-2">{opt}</span>
                        {isSelected ? (
                          <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                        ) : (
                          <Circle size={14} className="text-slate-300 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            if (currentQ.type === 'textarea') {
              return (
                <textarea
                  rows={2}
                  value={currentVal || ''}
                  onChange={(e) => handleProfilingAnswerChange(currentQ._id, e.target.value, 'textarea')}
                  placeholder="Type your response here..."
                  className="w-full text-xs p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary focus:outline-hidden text-slate-800 resize-none font-normal"
                />
              );
            }

            return (
              <input
                type="text"
                value={currentVal || ''}
                onChange={(e) => handleProfilingAnswerChange(currentQ._id, e.target.value, 'text')}
                placeholder="Type your response here..."
                className="w-full text-xs p-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-primary focus:outline-hidden text-slate-800 font-normal"
              />
            );
          })()}
        </div>

        {/* Stepper Actions */}
        <div className="shrink-0 flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            disabled={profilingCurrentIndex === 0}
            onClick={() => setProfilingCurrentIndex((prev) => Math.max(0, prev - 1))}
            className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-normal text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center gap-1"
          >
            <ChevronLeft size={13} />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-2">
            {profilingCurrentIndex < profilingQuestions.length - 1 ? (
              <button
                type="button"
                disabled={!isCurrentAnswered}
                onClick={() => setProfilingCurrentIndex((prev) => prev + 1)}
                className="px-4 py-1.5 rounded-xl bg-primary hover:bg-[#056564] text-white text-xs font-normal transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary disabled:active:scale-100"
              >
                <span>Next Question</span>
                <ChevronRight size={13} />
              </button>
            ) : (
              <button
                type="button"
                disabled={isProfilingSubmitting || !isCurrentAnswered}
                onClick={handleProfilingSubmit}
                className="px-4 py-1.5 rounded-xl bg-primary hover:bg-[#056564] text-white text-xs font-normal transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary disabled:active:scale-100"
              >
                {isProfilingSubmitting && <Loader2 size={12} className="animate-spin" />}
                <span>Save Profile</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center py-8 text-xs text-slate-400 font-normal">
      No setup questions available right now.
    </div>
  );
};

export default ProfilingTab;
